import { CaseData } from "@/cms/models";
import PriceDetail from "../PriceDetails";
import TextDetails from "../TextDetails";
import ExpenseDetail from "../ExpenseDetails";
import ConnectionFeesDetails from "../ConnectionFeesDetails";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/sharp-solid-svg-icons";
import { AnimatePresence, motion } from "framer-motion";
import { formatDate } from "@/utils/helper";

const  FullFinancialFacts: React.FC<{ caseData: CaseData, showStandardFinancing: boolean  }> = ({ caseData, showStandardFinancing }) => {
  return (
    <div className="p-4 bg-[var(--box-bg-color)] text-[var(--box-text-color)] homepage-text-sm">
      {caseData.priceInfo && !caseData.isRental && (
        <>
          <PriceDetail
            label="Pris"
            value={caseData.priceInfo ?? 0}
          >
            <Popover>
              {({ open }) => (
                <>
                  <PopoverButton>
                    {" "}
                    <FontAwesomeIcon
                      className="size-3.5 text-gray-400"
                      icon={faCircleInfo}
                    />
                  </PopoverButton>
                  <AnimatePresence>
                    {open && (
                      <PopoverPanel
                        static
                        as={motion.div}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        anchor={{ to: 'top start', gap: '-2px', offset: '-13px' }}
                        className="[--anchor-gap:2px]"
                      >
                        <div
                          className="z-20 text-sm p-3 mb-4 relative bg-[var(--button-active-bg-color)] text-[var(--button-bg-color)]">
                          <a
                            href="https://www.raadtilpenge.dk/Gode-raad/boliglaan/landingsside"
                            target="_blank"
                            aria-label="Tjek boliglån her"
                            className="hover:underline"
                          >
                            Tjek boliglån her
                          </a>
                          <span className="hidden text-[var(--button-active-bg-color)] lg:block absolute bottom-[-10px] left-[15%] transform -translate-x-1/2">
                            <svg
                              width="20"
                              height="10"
                              viewBox="0 0 20 10"
                              fill="currentColor"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M10 10L0 0H20L10 10Z"
                                fill="currentColor"
                              ></path>
                            </svg>
                          </span>
                        </div>
                      </PopoverPanel>
                    )}
                  </AnimatePresence>
                </>
              )}
            </Popover>
          </PriceDetail>
        </>
      )}
      {caseData.connectionFeesPaid && (
        <ConnectionFeesDetails label="Tilslutningsafgifter betalt" value={caseData.connectionFeesPaid} />
      )}
      {caseData.rentalTransferPrice > 0 && (
        <ExpenseDetail
          label="Afståelsessum"
          value={caseData.rentalTransferPrice}
        />
      )}
      {showStandardFinancing && caseData.downPayment > 0 && (
        <ExpenseDetail label="Udbetaling" value={caseData.downPayment} />
      )}
      {showStandardFinancing && caseData.totalMonthlyGrossPayment > 0 && (
        <ExpenseDetail
          label="Brutto (ex. ejer­ud­gif­ter)"
          value={caseData.totalMonthlyGrossPayment}
        />
      )}
      {showStandardFinancing && caseData.totalMonthlyNetPayment > 0 && (
        <ExpenseDetail
          label="Netto (ex. ejer­ud­gif­ter)"
          value={caseData.totalMonthlyNetPayment}
        />
      )}
      {caseData.ownerExpensesTotalMonthly > 0 && (
        <ExpenseDetail
          label="Ejerudgifter pr. md."
          value={caseData.ownerExpensesTotalMonthly}
        />
      )}
      {caseData.rentMonthly > 0 && (
        <ExpenseDetail label=" Leje pr. md." value={caseData.rentMonthly} />
      )}
      {caseData.rentAnnually > 0 && (
        <ExpenseDetail label=" Leje pr. år" value={caseData.rentAnnually} />
      )}
      {caseData.rentM2Annually > 0 && (
        <ExpenseDetail
          label=" Leje pr. m2 pr. år"
          value={caseData.rentM2Annually}
        />
      )}
      {caseData.rentAvailableFrom && (
        <TextDetails label="Ledig fra" value={formatDate(caseData.rentAvailableFrom)} />
      )}
      {caseData.rentAvailableTo && (
        <TextDetails label="Ledig til" value={formatDate(caseData.rentAvailableTo)} />
      )}
      {caseData.maxRentalPeriod > 0 && (
        <ExpenseDetail
          label="Maksimal udlejningsperiode (mdr.)"
          value={caseData.maxRentalPeriod}
        />
      )}
      {caseData.commonCostsAnnually > 0 && (
        <ExpenseDetail
          label="Fællesudgifter"
          value={caseData.commonCostsAnnually}
        />
      )}
      {caseData.consumptionCostsAnnually > 0 && (
        <ExpenseDetail
          label="Forbrugsudgifter"
          value={caseData.consumptionCostsAnnually}
        />
      )}
      {caseData.deposit > 0 && (
        <ExpenseDetail label="Depositum" value={caseData.deposit} />
      )}
      {caseData.prepaidRent > 0 && (
        <ExpenseDetail label="Forudbetalt leje" value={caseData.prepaidRent} />
      )}
      {caseData.consumptionCostsMonthly > 0 && (
        <ExpenseDetail
          label="A/C-forbrug pr. md."
          value={caseData.consumptionCostsMonthly}
        />
      )}
      {caseData.coopChargeMonthly > 0 && (
        <ExpenseDetail
          label="Boligydelse pr. md."
          value={caseData.coopChargeMonthly}
        />
      )}
      {caseData.heatingCosts > 0 && (
        <ExpenseDetail
          label="Varmeudgift pr. md."
          value={caseData.heatingCosts}
        />
      )}
      {caseData.operatingCostsAnnually > 0 && (
        <ExpenseDetail
          label="Driftsudgifter"
          value={caseData.operatingCostsAnnually}
        />
      )}
      {caseData.rentalIncomeAnnually > 0 && (
        <ExpenseDetail
          label="Lejeindtægter"
          value={caseData.rentalIncomeAnnually}
        />
      )}
      {caseData.rentalIncomeM2Annually > 0 && (
        <ExpenseDetail
          label="Lejeindtægter pr. m2"
          value={caseData.rentalIncomeM2Annually}
        />
      )}
      {caseData.rateOfReturn > 0 && (
        <ExpenseDetail label="Afkast i %" value={caseData.rateOfReturn} />
      )}
      {caseData.depreciation && (
        <div className="flex justify-between">
          <div className="flex items-center gap-1 propert-label">
            <span>Afskrivningsberettiget</span>
          </div>
          <div className="homepage-text-sm ">
            <span>
              {caseData.depreciation ? <span>Ja</span> : <span>Nej</span>}
            </span>
          </div>
        </div>
      )}
      {caseData.vatRegistered && (
        <div className="flex justify-between">
          <div className="flex items-center gap-1 propert-label">
            <span>Moms</span>
          </div>
          <div className="homepage-text-sm ">
            <span>
              {caseData.vatRegistered ? <span>Ja</span> : <span>Nej</span>}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FullFinancialFacts;
